import { useState, useMemo } from "react";
import JSZip from "jszip";
import { hlXML } from "../highlights/hlXML";
import type { RuleResult } from "../../types/sigwaz";
import { LEVEL_MAP } from "../../types/sigwaz";
import { CopyIcon, CheckIcon, DownloadIcon } from "../ui/Icons";

const FALLBACK_SLUG = "proc_creation_win_ps_download_cradle";

interface Props {
  result: RuleResult | null;
  batchXml: string | null;
  batchXmlFiles: string[];
  batchCount: number | null;
  batchErrors: number | null;
  ruleIdStart: number;
  yamlSlug?: string;
}

function sanitizeFilename(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "").slice(0, 60);
}

function dlBlob(content: string | Blob, filename: string, mimeType = "application/xml") {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function XmlOutput({ result, batchXml, batchXmlFiles, batchCount, batchErrors, ruleIdStart, yamlSlug }: Props) {
  const [copied, setCopied] = useState(false);

  const isSplit   = batchXmlFiles.length > 1;
  const activeXml = batchXml ?? result?.xml ?? "";
  const xmlHtml   = useMemo(() => (activeXml ? hlXML(activeXml) : ""), [activeXml]);
  const base      = yamlSlug || FALLBACK_SLUG;

  const xmlFilename = isSplit
    ? `${base}_rules.zip`
    : batchXml
      ? `${base}_rules.xml`
      : result
        ? `${sanitizeFilename(result.sigma_title) || base}_rules.xml`
        : `${base}_rules.xml`;

  const metaLabel = isSplit
    ? `${batchXmlFiles.length} files · ${batchCount ?? 0} rules`
    : batchXml
      ? `${batchCount ?? 0} rules${batchErrors ? ` · ${batchErrors} err` : ""}`
      : result
        ? `level ${LEVEL_MAP[result.sigma_level] ?? 10} · ${result.sigma_level}`
        : null;

  const copy = () => {
    if (!activeXml) return;
    navigator.clipboard?.writeText(activeXml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = async () => {
    if (!activeXml) return;
    if (isSplit) {
      const zip = new JSZip();
      batchXmlFiles.forEach((xml, i) => zip.file(`${base}_rules-${i + 1}.xml`, xml));
      const blob = await zip.generateAsync({ type: "blob" });
      dlBlob(blob, `${base}_rules.zip`, "application/zip");
    } else if (batchXml) {
      dlBlob(batchXml, `${base}_rules.xml`);
    } else if (result) {
      dlBlob(activeXml, `${sanitizeFilename(result.sigma_title) || base}_rules.xml`);
    }
  };

  const downloadLabel = isSplit ? "↓ Download .zip" : "↓ Download";

  return (
    <>
      <div className="panel-hdr">
        <span className="lang-badge lb-xml">XML · Wazuh</span>
        <span className="panel-fname">{xmlFilename}</span>
        {metaLabel && <span className="panel-meta">{metaLabel}</span>}
        {activeXml && (
          <div className="panel-btns">
            {!isSplit && (
              <button className={`phbtn${copied ? " ok" : ""}`} onClick={copy}>
                {copied ? <><CheckIcon size={11} /> Copied</> : <><CopyIcon size={11} /> Copy</>}
              </button>
            )}
            <button className="phbtn" onClick={handleDownload}>
              <DownloadIcon size={11} /> {isSplit ? "Download .zip" : "Download"}
            </button>
          </div>
        )}
      </div>

      {activeXml ? (
        <div className="xml-out fade-in">
          {isSplit && (
            <div style={{
              padding: "6px 14px", fontSize: 11, color: "var(--t3)",
              background: "rgba(var(--accent-rgb),.06)", borderBottom: "1px solid var(--bd)",
            }}>
              Showing file 1/{batchXmlFiles.length}. Download the .zip to get all files
            </div>
          )}
          <pre className="xml-pre" dangerouslySetInnerHTML={{ __html: xmlHtml }} />
        </div>
      ) : (batchErrors != null && batchErrors > 0 && !batchXml) ? (
        <div className="empty-out">
          <div className="empty-glyph" style={{ color: "#ef4444" }}>✕</div>
          <p className="empty-title" style={{ color: "#ef4444" }}>All rules failed to convert</p>
          <p className="empty-sub" style={{ color: "#ef4444" }}>
            {batchErrors} rule{batchErrors > 1 ? "s" : ""} had errors. Check the YAML syntax.
          </p>
        </div>
      ) : result?.errors?.length ? (
        <div className="empty-out">
          <div className="empty-glyph" style={{ color: "#ef4444" }}>✕</div>
          <p className="empty-title" style={{ color: "#ef4444" }}>Conversion failed</p>
          {result.errors.map((e, i) => (
            <p key={i} className="empty-sub" style={{ color: "#ef4444", maxWidth: 260 }}>{e}</p>
          ))}
        </div>
      ) : (
        <div className="empty-out">
          <div className="empty-glyph">⎘</div>
          <p className="empty-title">Wazuh XML will appear here</p>
          <p className="empty-sub">
            Paste a Sigma rule, then press <strong>Convert</strong> or use{" "}
            <span className="empty-kbd">
              <span className="ekbd">⌘</span>
              <span className="ekbd">↩</span>
            </span>
          </p>
        </div>
      )}
    </>
  );
}
